import React, { useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import axios from 'axios';
import { useDebounce } from 'use-debounce';
import { LuSearch } from 'react-icons/lu';
import { IoCloseCircle } from 'react-icons/io5';
import { FaPeopleGroup } from 'react-icons/fa6';
import { TbShoppingBagPlus } from 'react-icons/tb';
import { MdOutlineDynamicFeed } from 'react-icons/md';
import DarkModeContext from './DarkModeContext';
import CardModel from './CardModel';
import CardPack from './CardPack';
import ProfileFeedList from './ProfileFeedList';
import Spinner from './Spinner';
import FluidMediaModal2 from './FluidMediaModal2';
import AuthRequiredModal from './AuthRequiredModal';
import { requireAgeVerification } from './AgeVerificationModal';
import config from '../Config';
import { authUserAtom, isAuthAtom, dataUserAtom } from '../Services/store/atoms';

const DASHBOARD_BASES = ['model', 'client', 'admin', 'user', 'assistant'];

// Pastilla de descubrimiento (Modelos/Packs/Feeds), calcada en comportamiento
// de la que usa ProfileModel para sus tabs: mismo estilo visual y misma idea
// de swap de contenido in place. Se usa tanto en el home del cliente logueado
// (/client) como en la landing pública (/) — de ahí que la navegación a
// perfiles/compras se resuelva según el contexto (dashboard vs. público) en
// vez de hardcodear /client/....
function DiscoverTabs() {
    const history = useHistory();
    const location = useLocation();
    const { darkMode } = useContext(DarkModeContext);
    const [authUser] = useAtom(authUserAtom);
    const [isAuth] = useAtom(isAuthAtom);
    const [dataUser] = useAtom(dataUserAtom);

    const [activeTab, setActiveTab] = useState('models');
    const [showSearch, setShowSearch] = useState(false);

    // Modelos
    const [modelSearch, setModelSearch] = useState('');
    const [modelSearchValue] = useDebounce(modelSearch, 600);
    const [models, setModels] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [modelsPage, setModelsPage] = useState(1);
    const [modelsHasMore, setModelsHasMore] = useState(true);
    const modelsSeed = useRef(Math.floor(Math.random() * 1000000));

    // Packs
    const [packSearch, setPackSearch] = useState('');
    const [packSearchValue] = useDebounce(packSearch, 600);
    const [packs, setPacks] = useState([]);
    const [packsLoading, setPacksLoading] = useState(false);
    const [packsPage, setPacksPage] = useState(1);
    const [packsHasMore, setPacksHasMore] = useState(true);
    const packsLoadedRef = useRef(false);

    // Feeds (sin búsqueda del lado del servidor: se filtra client-side lo ya cargado)
    const [feedSearch, setFeedSearch] = useState('');
    const [feeds, setFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(false);
    const [feedsNextPageUrl, setFeedsNextPageUrl] = useState(null);
    const feedsLoadedRef = useRef(false);

    const fetchModels = useCallback((page = 1) => {
        setModelsLoading(true);
        const requestData = new URLSearchParams();
        if (modelSearchValue.trim()) requestData.append('search', modelSearchValue.trim());
        requestData.append('page', page);
        requestData.append('seed', modelsSeed.current);

        axios.post(`${config.REACT_APP_BASE_URL}/api/v1/packs/listModel`, requestData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                Authorization: `Bearer ${authUser?.token}`,
            },
        })
            .then((response) => {
                setModels(prev => page === 1 ? response.data.data : [...prev, ...response.data.data]);
                setModelsHasMore(response.data.current_page < response.data.last_page);
                setModelsPage(response.data.current_page);
            })
            .catch((error) => console.error('Error fetching models:', error))
            .finally(() => setModelsLoading(false));
    }, [authUser?.token, modelSearchValue]);

    const fetchPacks = useCallback((page = 1) => {
        setPacksLoading(true);
        const requestData = new URLSearchParams();
        if (packSearchValue.trim()) requestData.append('search', packSearchValue.trim());
        requestData.append('page', page);

        axios.post(`${config.REACT_APP_BASE_URL}/api/v1/packs/list`, requestData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                Authorization: `Bearer ${authUser?.token}`,
            },
        })
            .then((response) => {
                setPacks(prev => page === 1 ? response.data.data : [...prev, ...response.data.data]);
                setPacksHasMore(response.data.current_page < response.data.last_page);
                setPacksPage(response.data.current_page);
            })
            .catch((error) => console.error('Error fetching packs:', error))
            .finally(() => setPacksLoading(false));
    }, [authUser?.token, packSearchValue]);

    const fetchFeeds = useCallback((pageUrl, append = false) => {
        setFeedsLoading(true);
        const endpoint = isAuth
            ? `${config.REACT_APP_BASE_URL}/api/v1/home/FeedPublicAuth`
            : `${config.REACT_APP_BASE_URL}/api/v1/home/FeedPublic`;

        axios.post(pageUrl || endpoint, { genre: 'All' }, {
            headers: { Authorization: `Bearer ${authUser?.token}` },
        })
            .then((response) => {
                setFeeds(prev => {
                    const newFeeds = response.data.data;
                    if (!append) return newFeeds;
                    return [...prev, ...newFeeds.filter(f => !prev.some(p => p.id === f.id))];
                });
                setFeedsNextPageUrl(response.data.next_page_url);
            })
            .catch((error) => console.error('Error fetching feeds:', error))
            .finally(() => setFeedsLoading(false));
    }, [isAuth, authUser?.token]);

    // Modelos: es la tab por defecto, se carga apenas entra a Home.
    useEffect(() => {
        fetchModels(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelSearchValue]);

    // Packs: carga perezosa recién la primera vez que se activa esa tab.
    // OJO: antes este efecto dependía de [activeTab, packSearchValue] con un
    // solo guard basado en packsLoadedRef — una vez cargada una vez, ese
    // guard queda permanentemente abierto y el efecto vuelve a disparar
    // fetchPacks(1) en CADA cambio de tab (aunque se cambie a Modelos o
    // Feeds), reseteando la lista a la página 1 de la nada. Separado en dos
    // efectos: uno solo para la carga inicial perezosa (depende de
    // activeTab), y otro solo para recargar cuando cambia la búsqueda
    // (depende de packSearchValue, no de activeTab).
    useEffect(() => {
        if (activeTab !== 'packs' || packsLoadedRef.current) return;
        packsLoadedRef.current = true;
        fetchPacks(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (!packsLoadedRef.current) return; // todavía no se visitó la tab
        fetchPacks(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packSearchValue]);

    useEffect(() => {
        if (activeTab !== 'feeds' && !feedsLoadedRef.current) return;
        if (feedsLoadedRef.current) return; // sin búsqueda server-side, no hay por qué recargar
        feedsLoadedRef.current = true;
        fetchFeeds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const modelsObserver = useRef();
    const lastModelRef = useCallback((node) => {
        if (modelsLoading || !modelsHasMore) return;
        if (modelsObserver.current) modelsObserver.current.disconnect();
        modelsObserver.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) fetchModels(modelsPage + 1);
        });
        if (node) modelsObserver.current.observe(node);
    }, [modelsLoading, modelsHasMore, modelsPage, fetchModels]);

    const packsObserver = useRef();
    const lastPackRef = useCallback((node) => {
        if (packsLoading || !packsHasMore) return;
        if (packsObserver.current) packsObserver.current.disconnect();
        packsObserver.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) fetchPacks(packsPage + 1);
        });
        if (node) packsObserver.current.observe(node);
    }, [packsLoading, packsHasMore, packsPage, fetchPacks]);

    // Mismo criterio que LandingWithoutHeader: si venimos de un pathname de
    // dashboard (/client, /model, ...) el perfil se abre bajo ese mismo
    // prefijo; si no (landing pública), se abre la variante sin prefijo.
    const clickModel = (userId) => {
        const origin = location.state?.background || location;
        const base = origin.pathname.split('/')[1];
        if (DASHBOARD_BASES.includes(base)) {
            history.push(`/${base}/profileModal/${userId}`, { background: origin });
        } else {
            history.push(`/profileModal/${userId}`, { background: origin });
        }
    };

    const [selectedPack, setSelectedPack] = useState(null);
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);

    const clickPack = (pack) => {
        if (!isAuth) {
            setShowLoginModal(true);
            return;
        }
        setSelectedPack(pack);
        setShowBuyModal(true);
    };

    const handleBuyPack = () => {
        setShowBuyModal(false);
        const buyRole = ['client', 'user'].includes(dataUser?.role) ? dataUser.role : 'client';
        history.push(`/${buyRole}/buyPack/${selectedPack.pack_id}`);
    };

    const handleMediaClick = (mediaUrls) => {
        setSelectedMediaUrls(mediaUrls);
        setShowMediaModal(true);
    };

    const searchPlaceholders = {
        models: 'Buscar modelos...',
        packs: 'Buscar packs...',
        feeds: 'Buscar en feeds...',
    };

    const searchValues = { models: modelSearch, packs: packSearch, feeds: feedSearch };
    const searchSetters = { models: setModelSearch, packs: setPackSearch, feeds: setFeedSearch };

    const visibleFeeds = feedSearch.trim()
        ? feeds.filter((f) => {
            const needle = feedSearch.trim().toLowerCase();
            return (f.description || '').toLowerCase().includes(needle)
                || (f.user_date?.nickname || '').toLowerCase().includes(needle);
        })
        : feeds;

    const discoverTabs = [
        { id: 'models', label: 'Modelos', icon: <FaPeopleGroup className="text-sm sm:text-base shrink-0" /> },
        { id: 'packs', label: 'Packs', icon: <TbShoppingBagPlus className="text-sm sm:text-base shrink-0" /> },
        { id: 'feeds', label: 'Feeds', icon: <MdOutlineDynamicFeed className="text-sm sm:text-base shrink-0" /> },
    ];

    // Control segmentado de iOS + botón de búsqueda redondo; la búsqueda se
    // abre como el campo redondeado de iOS debajo.
    const discoverPill = (
        <div className="flex flex-col items-center gap-2">
            <div className="flex w-full max-w-md items-center gap-2">
                <div className="ap-segmented flex-1" role="tablist">
                    {discoverTabs.map((tab) => (
                        <button
                            key={tab.id}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            onClick={() => {
                                // Packs y Feeds son el mismo contenido que /packs y /feed.
                                if (tab.id !== 'models') requireAgeVerification();
                                setActiveTab(tab.id);
                            }}
                            className="ap-segment min-w-0 px-1.5 sm:px-3 overflow-hidden"
                        >
                            {tab.icon}
                            <span className="truncate">{tab.label}</span>
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setShowSearch((prev) => !prev)}
                    aria-label="Buscar"
                    aria-pressed={showSearch}
                    className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full transition-colors"
                    style={showSearch
                        ? { background: 'var(--ap-tint)', color: '#fff' }
                        : { background: 'var(--ap-fill)', color: 'var(--ap-label-2)' }}
                >
                    <LuSearch className="text-base" />
                </button>
            </div>

            {showSearch && (
                <label className="ap-search w-full max-w-md">
                    <LuSearch className="shrink-0" />
                    <input
                        type="search"
                        autoFocus
                        value={searchValues[activeTab]}
                        onChange={(e) => searchSetters[activeTab](e.target.value)}
                        placeholder={searchPlaceholders[activeTab]}
                        aria-label={searchPlaceholders[activeTab]}
                    />
                    {searchValues[activeTab] && (
                        <button type="button" onClick={() => searchSetters[activeTab]('')} aria-label="Limpiar búsqueda" className="shrink-0">
                            <IoCloseCircle className="text-lg" style={{ color: 'var(--ap-label-3)' }} />
                        </button>
                    )}
                </label>
            )}
        </div>
    );

    const modelsBody = (
        <div className="pt-4">
            {models.length === 0 && !modelsLoading ? (
                <div className="ap-subhead text-center py-16">No hay modelos para mostrar.</div>
            ) : (
                <div className="flex flex-wrap justify-center gap-4">
                    {models.map((model, index) => (
                        <div
                            key={model.user_id}
                            ref={index === models.length - 1 ? lastModelRef : null}
                            className="cursor-pointer"
                            onClick={() => clickModel(model.user_id)}
                        >
                            <CardModel model={model} index={index} />
                        </div>
                    ))}
                </div>
            )}
            {modelsLoading && <div className="flex justify-center py-6"><Spinner /></div>}
        </div>
    );

    const packsBody = (
        <div className="pt-4">
            {packs.length === 0 && !packsLoading ? (
                <div className="ap-subhead text-center py-16">No hay packs para mostrar.</div>
            ) : (
                <div className="flex flex-wrap justify-center gap-4">
                    {packs.map((pack, index) => (
                        <div key={pack.pack_id} ref={index === packs.length - 1 ? lastPackRef : null} className="cursor-pointer">
                            <CardPack pack={pack} onClick={clickPack} onMediaClick={handleMediaClick} />
                        </div>
                    ))}
                </div>
            )}
            {packsLoading && <div className="flex justify-center py-6"><Spinner /></div>}
        </div>
    );

    const feedsBody = (
        <div className="pt-4">
            <ProfileFeedList
                feeds={visibleFeeds}
                isLoading={feedsLoading}
                hasMore={!!feedsNextPageUrl}
                onLoadMore={() => fetchFeeds(feedsNextPageUrl, true)}
            />
        </div>
    );

    return (
        <>
            {/* Mismo fondo que la página (DashboardLayout): con material translúcido se veía una franja más clara. */}
            <div className="sticky top-14 z-30 -mx-2 lg:-mx-4 px-4 pt-3 pb-3 bg-gray-100 dark:bg-gray-900">
                {discoverPill}
            </div>
            <div className="px-4 pb-10">
                {activeTab === 'models' && modelsBody}
                {activeTab === 'packs' && packsBody}
                {activeTab === 'feeds' && feedsBody}
            </div>

            {showBuyModal && (
                <div className="fixed z-confirm inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-dvh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-black/40 transition-opacity" aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">&#8203;</span>
                        <div className="ap-card inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full" style={{ boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
                            <div className="p-5 flex flex-col">
                                <div className="flex flex-row justify-between items-center">
                                    <img
                                        src={selectedPack?.files?.length > 0 ? (selectedPack.files[0].preview_file || selectedPack.files[0].processed_file) : 'https://via.placeholder.com/150'}
                                        alt={selectedPack?.name}
                                        className="w-24 h-24 object-cover rounded-[14px]"
                                    />
                                    <div className="flex flex-col ml-4">
                                        <h3 className="ap-headline" id="modal-title">
                                            {selectedPack?.name}
                                        </h3>
                                        <p className="ap-subhead mt-1">
                                            {selectedPack?.tokens} coins
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="px-5 pb-5 flex flex-col sm:flex-row-reverse gap-3">
                                <button
                                    type="button"
                                    className="ap-btn ap-btn-filled w-full sm:flex-1"
                                    onClick={handleBuyPack}
                                >
                                    Comprar
                                </button>
                                <button
                                    type="button"
                                    className="ap-btn ap-btn-gray w-full sm:flex-1"
                                    onClick={() => setShowBuyModal(false)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showMediaModal && (
                <FluidMediaModal2
                    show={showMediaModal}
                    onClose={() => setShowMediaModal(false)}
                    mediaUrls={selectedMediaUrls}
                />
            )}

            <AuthRequiredModal
                show={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                title="Acceso Restringido"
                message="Debes iniciar sesión para comprar este pack."
            />
        </>
    );
}

export default DiscoverTabs;
